import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useToast } from '@/shared/ui/Toast';
import {
  agencyApi,
  type PipelineDetail,
  type PipelineStageStatus,
  type PipelineStepMeta,
} from '../api/agencyApi';
import { usePractice } from '../hooks/usePractices';
import { agencyKeys } from '../queryKeys';
import styles from './LocationOnboardingPage.module.css';

type PipelineStage = 'crawl' | 'extraction' | 'audit' | 'competitors';

const STAGE_ORDER: PipelineStage[] = ['crawl', 'extraction', 'competitors', 'audit'];

const STAGE_INFO: Record<PipelineStage, { label: string; description: string }> = {
  crawl: { label: 'Website Crawl', description: 'Crawling website pages and collecting data' },
  extraction: {
    label: 'Data Extraction',
    description: 'Extracting practice information from website content',
  },
  audit: { label: 'SEO Audit', description: 'Running comprehensive SEO and AEO analysis' },
  competitors: {
    label: 'Competitor Discovery',
    description: 'Finding and analyzing local competitors',
  },
};

function StepIcon({
  status,
}: {
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'blocked';
}) {
  if (status === 'completed') return <div className={styles.iconDone}>&#10003;</div>;
  if (status === 'failed') return <div className={styles.iconFailed}>&#10007;</div>;
  if (status === 'in_progress') return <div className={styles.iconRunning} />;
  if (status === 'blocked') return <div className={styles.iconBlocked} />;
  return <div className={styles.iconPending} />;
}

function getDisplayStatus(
  stageStatus: PipelineStageStatus | undefined,
  isBlocked: boolean,
): 'pending' | 'in_progress' | 'completed' | 'failed' | 'blocked' {
  if (isBlocked) return 'blocked';
  return stageStatus ?? 'pending';
}

// ─── Step detail renderers ──────────────────────────────────────────────────

function CrawlSubStepIcon({ status }: { status: string }) {
  if (status === 'warn') return <span className={styles.subStepWarn}>&#9888;</span>;
  if (status === 'done') return <span className={styles.subStepDone}>&#10003;</span>;
  if (status === 'skipped') return <span className={styles.subStepSkipped}>&#8211;</span>;
  if (status === 'failed') return <span className={styles.subStepFailed}>&#10007;</span>;
  if (status === 'running') return <span className={styles.subStepRunning} />;
  return <span className={styles.subStepPending} />;
}

function CrawlDetail({ meta, websiteRoot }: { meta: PipelineStepMeta; websiteRoot?: string }) {
  const crawl = meta.crawl;
  if (!crawl) return null;

  // Strip the website root from URLs to show just the path
  const stripRoot = (url: string) => {
    if (!websiteRoot) return url;
    const root = websiteRoot.replace(/\/+$/, '');
    if (url.startsWith(root)) {
      const path = url.slice(root.length);
      return path || '/';
    }
    return url;
  };

  const hasProgress = crawl.progress && crawl.progress.length > 0;
  const isCrawlStep = (id: string) =>
    id === 'crawl_pages' ||
    id === 'crawl_pages_http' ||
    id === 'crawl_pages_playwright' ||
    id === 'crawl_pages_firecrawl';

  const wrapperStep = crawl.progress?.find((s) => s.id === 'crawl_pages');
  const usedTransport =
    (wrapperStep?.meta?.transport as 'http' | 'playwright' | 'firecrawl' | undefined) ??
    (crawl.progress
      ?.find((s) => s.status === 'done' && s.id.startsWith('crawl_pages_'))
      ?.id.replace('crawl_pages_', '') as 'http' | 'playwright' | 'firecrawl' | undefined);
  const didEscalate = usedTransport !== undefined && usedTransport !== 'http';

  const anySubStepFailed = crawl.progress?.some(
    (s) => s.id.startsWith('crawl_pages_') && s.status === 'failed',
  );

  return (
    <div className={styles.stepMeta}>
      {websiteRoot && (
        <div className={styles.metaLabel}>
          Crawling <span className={styles.crawlTargetUrl}>{websiteRoot}</span>
        </div>
      )}
      {usedTransport === 'playwright' && (
        <div className={styles.metaNotice}>
          Website uses JavaScript rendering (React/SPA). Standard crawl returned empty content —
          re-crawled with browser rendering via Playwright.
        </div>
      )}
      {usedTransport === 'firecrawl' && (
        <div className={styles.metaNotice}>
          Host&apos;s WAF blocked our direct and browser crawls. Re-crawled via Firecrawl using
          rotating residential IPs.
        </div>
      )}
      {hasProgress && (
        <div className={styles.subSteps}>
          {crawl.progress!.map((step) => {
            const displayStatus =
              step.id === 'crawl_pages' && step.status === 'done' && anySubStepFailed
                ? 'warn'
                : step.status;
            const pageCount =
              step.id === 'crawl_pages'
                ? ((step.meta?.pagesCrawled as number | undefined) ?? crawl.pageCount)
                : (step.meta?.pagesCrawled as number | undefined);
            const showPageCount = isCrawlStep(step.id) && pageCount !== undefined && pageCount > 0;
            const showUrlList =
              isCrawlStep(step.id) &&
              step.status === 'done' &&
              crawl.urls.length > 0 &&
              !(step.id === 'crawl_pages' && didEscalate);
            return (
              <div key={step.id}>
                <div className={styles.subStep}>
                  <CrawlSubStepIcon status={displayStatus} />
                  <span className={styles.subStepLabel}>
                    {step.label}
                    {showPageCount && (
                      <span className={styles.subStepMeta}>
                        {' '}
                        &middot; {pageCount} page{pageCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                  {step.error && <span className={styles.subStepError}>{step.error}</span>}
                </div>
                {showUrlList && (
                  <ul className={styles.subStepUrlList}>
                    {crawl.urls.map((url) => (
                      <li key={url} className={styles.metaListItem}>
                        {stripRoot(url)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExtractionDetail({ meta }: { meta: PipelineStepMeta }) {
  const ext = meta.extraction;
  if (!ext) return null;

  const fields: Array<{ label: string; value: string | null }> = [
    { label: 'Practice Name', value: ext.practiceName },
    { label: 'Address', value: ext.structuredAddress?.full ?? null },
    { label: 'Phone', value: ext.phones.length > 0 ? ext.phones.join(', ') : null },
    { label: 'Services', value: ext.services.length > 0 ? ext.services.join(', ') : null },
    { label: 'Providers', value: ext.providers.length > 0 ? ext.providers.join(', ') : null },
    { label: 'Technology', value: ext.technology.length > 0 ? ext.technology.join(', ') : null },
    { label: 'Insurance', value: ext.insurances.length > 0 ? ext.insurances.join(', ') : null },
    { label: 'Languages', value: ext.languages.length > 0 ? ext.languages.join(', ') : null },
    {
      label: 'Membership Plans',
      value: ext.membershipPlans.length > 0 ? ext.membershipPlans.join(', ') : null,
    },
    {
      label: 'Payment Options',
      value: ext.paymentOptions.length > 0 ? ext.paymentOptions.join(', ') : null,
    },
    { label: 'Office Hours', value: ext.hasOfficeHours ? 'Found' : null },
  ];

  const found = fields.filter((f) => f.value);
  const missing = fields.filter((f) => !f.value);

  return (
    <div className={styles.stepMeta}>
      <div className={styles.metaLabel}>
        {found.length} of {fields.length} fields extracted
      </div>
      <div className={styles.metaGrid}>
        {found.map((f) => (
          <div key={f.label} className={styles.metaField}>
            <span className={styles.metaFieldLabel}>{f.label}</span>
            <span className={styles.metaFieldValue}>{f.value}</span>
          </div>
        ))}
      </div>
      {missing.length > 0 && (
        <div className={styles.metaMissing}>
          Not found: {missing.map((f) => f.label).join(', ')}
        </div>
      )}
    </div>
  );
}

function AuditDetail({ meta, status }: { meta: PipelineStepMeta; status?: string }) {
  const audit = meta.audit;
  if (!audit) return null;

  const isRunning = status === 'in_progress';
  const hasProgress = audit.progress && audit.progress.length > 0;
  const hasScore = audit.overallScore != null;

  return (
    <div className={styles.stepMeta}>
      {hasProgress && (
        <div className={styles.subSteps}>
          {audit.progress!.map((step) => {
            const reason = step.meta?.reason as string | undefined;
            const reasonLabel =
              reason === 'not_connected'
                ? 'Not connected'
                : reason === 'cached'
                  ? 'Using cached data'
                  : null;
            return (
              <div key={step.id} className={styles.subStep}>
                <CrawlSubStepIcon status={step.status} />
                <span className={styles.subStepLabel}>
                  {step.label}
                  {reasonLabel && (
                    <span className={styles.subStepMeta}> &middot; {reasonLabel}</span>
                  )}
                </span>
                {step.error && <span className={styles.subStepError}>{step.error}</span>}
              </div>
            );
          })}
        </div>
      )}
      {!isRunning && hasScore && (
        <div className={styles.metaLabel}>
          Score: {audit.overallScore} ({audit.grade ?? '—'})
        </div>
      )}
      {!isRunning && audit.summary && <div className={styles.metaSummary}>{audit.summary}</div>}
    </div>
  );
}

function CompetitorsDetail({ meta }: { meta: PipelineStepMeta }) {
  const comp = meta.competitors;
  if (!comp) return null;
  return (
    <div className={styles.stepMeta}>
      <div className={styles.metaLabel}>
        {comp.count} competitor{comp.count !== 1 ? 's' : ''} found nearby
      </div>
    </div>
  );
}

const STEP_DETAIL: Record<
  PipelineStage,
  (props: { meta: PipelineStepMeta; websiteRoot?: string; status?: string }) => JSX.Element | null
> = {
  crawl: CrawlDetail,
  extraction: ExtractionDetail,
  audit: AuditDetail,
  competitors: CompetitorsDetail,
};

// ─── Page ───────────────────────────────────────────────────────────────────

export function LocationOnboardingPage() {
  const { practiceId, locationId } = useParams<{ practiceId: string; locationId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const { data: practice } = usePractice(practiceId ?? '');

  const queryKey = ['agency', 'pipeline', practiceId, locationId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => agencyApi.getLocationPipeline(practiceId!, locationId!),
    enabled: !!practiceId && !!locationId,
    refetchInterval: (query) => {
      const detail = query.state.data?.data;
      if (!detail) return false;
      const hasInProgress = STAGE_ORDER.some((s) => detail.status[s] === 'in_progress');
      return hasInProgress ? 5000 : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: (mode: 'start' | 'retry' | 'restart' | 'cancel') =>
      agencyApi.startOnboarding(practiceId!, locationId!, mode),
    onSuccess: (result, mode) => {
      // Reset cache immediately so UI doesn't show stale data from previous run
      if (result.data?.pipelineStatus) {
        queryClient.setQueryData(queryKey, (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          const prev = old as { data?: PipelineDetail };
          return {
            ...old,
            data: {
              ...prev.data,
              status: result.data!.pipelineStatus,
              errors: {},
              meta: {},
            },
          };
        });
      }
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId!) });
      toast.success(
        mode === 'restart'
          ? 'Pipeline restarted.'
          : mode === 'retry'
            ? 'Pipeline resumed.'
            : mode === 'cancel'
              ? 'Pipeline cancelled.'
              : 'Onboarding started.',
      );
      setConfirmRestart(false);
      setConfirmCancel(false);
    },
    onError: () => toast.error('Failed to start pipeline.'),
  });

  const runStageMutation = useMutation({
    mutationFn: (stage: PipelineStage) =>
      agencyApi.startOnboarding(practiceId!, locationId!, 'run_stage', stage),
    onSuccess: (result, stage) => {
      if (result.data?.pipelineStatus) {
        queryClient.setQueryData(queryKey, (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          return { ...old, status: result.data!.pipelineStatus };
        });
      }
      queryClient.invalidateQueries({ queryKey });
      toast.success(`${STAGE_INFO[stage].label} started.`);
    },
    onError: () => toast.error('Failed to run stage.'),
  });

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    );
  }

  const detail = data?.data;
  if (!detail) {
    return (
      <div className={styles.page}>
        <button
          className={styles.backBtn}
          onClick={() => navigate(`/agency/practices/${practiceId}`)}
        >
          &#8592; Back to Practice
        </button>
        <p>Location not found.</p>
      </div>
    );
  }

  const { status: pipelineStatus, errors, hasWebsite, meta } = detail;

  const hasAnyStage = STAGE_ORDER.some((s) => pipelineStatus[s] != null);
  const hasFailed = STAGE_ORDER.some((s) => pipelineStatus[s] === 'failed');
  const hasInProgress = STAGE_ORDER.some((s) => pipelineStatus[s] === 'in_progress');
  const allCompleted = STAGE_ORDER.every((s) => pipelineStatus[s] === 'completed');

  const firstFailedIdx = STAGE_ORDER.findIndex((s) => pipelineStatus[s] === 'failed');

  const doneCount = STAGE_ORDER.filter((s) => pipelineStatus[s] === 'completed').length;
  const pct = hasAnyStage ? Math.round((doneCount / STAGE_ORDER.length) * 100) : 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button
          className={styles.backBtn}
          onClick={() => navigate(`/agency/practices/${practiceId}`)}
        >
          &#8592; Back to Practice
        </button>
        <h1 className={styles.title}>
          {practice?.name ?? ''}
          {detail.name !== practice?.name ? ` — ${detail.name}` : ''}
        </h1>
        <p className={styles.subtitle}>Onboarding</p>
      </div>

      {!hasWebsite ? (
        <EmptyState
          icon={<Globe size={32} strokeWidth={1.5} aria-hidden />}
          title="No website configured"
          description="Add a website URL to this location before running the onboarding pipeline."
          actions={
            <Button variant="secondary" onClick={() => navigate(`/agency/practices/${practiceId}`)}>
              Edit Location
            </Button>
          }
        />
      ) : allCompleted ? (
        <>
          {/* Show completed steps with details */}
          <Card>
            <div className={styles.stepsContainer}>
              {STAGE_ORDER.map((stage) => {
                const info = STAGE_INFO[stage];
                const DetailComponent = STEP_DETAIL[stage];
                return (
                  <div key={stage} className={styles.stepCard}>
                    <div className={styles.stepIcon}>
                      <StepIcon status="completed" />
                    </div>
                    <div className={styles.stepBody}>
                      <div className={styles.stepLabel}>{info.label}</div>
                      <div className={`${styles.stepStatus} ${styles.stepStatusDone}`}>
                        Completed
                      </div>
                      <DetailComponent meta={meta} websiteRoot={detail.website ?? undefined} />
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={runStageMutation.isPending && runStageMutation.variables === stage}
                        disabled={runStageMutation.isPending}
                        onClick={() => runStageMutation.mutate(stage)}
                      >
                        Run
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <div className={styles.actions}>
            <Button onClick={() => navigate(`/agency/practices/${practiceId}`)}>
              Back to Practice
            </Button>
            {confirmRestart ? (
              <>
                <p className={styles.metaNotice}>
                  This restarts onboarding for this location. It does not reset the agents — they
                  keep their memory of this practice. To start an agent over, unassign and reassign
                  it (Reset &amp; rehire) on the Agents tab.
                </p>
                <Button size="sm" variant="ghost" onClick={() => setConfirmRestart(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  loading={startMutation.isPending}
                  onClick={() => startMutation.mutate('restart')}
                >
                  Confirm Reset
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmRestart(true)}
                disabled={startMutation.isPending}
              >
                Hard Reset
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Progress bar */}
          {hasAnyStage && (
            <div className={styles.progressSection}>
              <div className={styles.progressLabel}>
                {hasFailed
                  ? 'Pipeline stopped'
                  : hasInProgress
                    ? 'In progress...'
                    : `${pct}% complete`}
              </div>
              <div className={styles.progressBar}>
                <div
                  className={`${styles.progressFill} ${hasFailed ? styles.progressFillError : ''}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Steps */}
          <Card>
            <div className={styles.stepsContainer}>
              {STAGE_ORDER.map((stage, idx) => {
                const isBlocked =
                  firstFailedIdx !== -1 &&
                  idx > firstFailedIdx &&
                  pipelineStatus[stage] !== 'completed';
                const displayStatus = getDisplayStatus(pipelineStatus[stage], isBlocked);
                const info = STAGE_INFO[stage];
                const error = errors[stage];
                const DetailComponent = STEP_DETAIL[stage];

                return (
                  <div key={stage} className={styles.stepCard}>
                    <div className={styles.stepIcon}>
                      <StepIcon status={displayStatus} />
                    </div>
                    <div className={styles.stepBody}>
                      <div
                        className={`${styles.stepLabel} ${displayStatus === 'blocked' ? styles.stepLabelBlocked : ''}`}
                      >
                        {info.label}
                      </div>
                      <div
                        className={`${styles.stepDescription} ${displayStatus === 'blocked' ? styles.stepDescriptionBlocked : ''}`}
                      >
                        {info.description}
                      </div>
                      {displayStatus === 'in_progress' && (
                        <div className={`${styles.stepStatus} ${styles.stepStatusRunning}`}>
                          Running...
                        </div>
                      )}
                      {displayStatus === 'completed' && (
                        <div className={`${styles.stepStatus} ${styles.stepStatusDone}`}>
                          Completed
                        </div>
                      )}
                      {displayStatus === 'failed' && (
                        <div className={`${styles.stepStatus} ${styles.stepStatusFailed}`}>
                          Failed
                        </div>
                      )}
                      {(displayStatus === 'completed' ||
                        displayStatus === 'failed' ||
                        displayStatus === 'in_progress') && (
                        <DetailComponent
                          meta={meta}
                          websiteRoot={detail.website ?? undefined}
                          status={displayStatus}
                        />
                      )}
                      {displayStatus === 'failed' && error && (
                        <div className={styles.errorSection}>
                          <div className={styles.errorTitle}>Error Details</div>
                          <div className={styles.errorTrace}>{error}</div>
                        </div>
                      )}
                      {!hasInProgress && displayStatus !== 'in_progress' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={
                            runStageMutation.isPending && runStageMutation.variables === stage
                          }
                          disabled={runStageMutation.isPending}
                          onClick={() => runStageMutation.mutate(stage)}
                        >
                          Run
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Action buttons */}
          <div className={styles.actions}>
            {hasInProgress &&
              (confirmCancel ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>
                    Keep Running
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    loading={startMutation.isPending}
                    onClick={() => startMutation.mutate('cancel')}
                  >
                    Confirm Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmCancel(true)}
                  disabled={startMutation.isPending}
                >
                  Cancel Pipeline
                </Button>
              ))}
            {!hasAnyStage && (
              <Button
                loading={startMutation.isPending}
                onClick={() => startMutation.mutate('start')}
              >
                Start Onboarding
              </Button>
            )}
            {hasFailed && (
              <Button
                loading={startMutation.isPending}
                onClick={() => startMutation.mutate('retry')}
              >
                Resume
              </Button>
            )}
            {hasAnyStage &&
              !hasInProgress &&
              (confirmRestart ? (
                <>
                  <p className={styles.metaNotice}>
                    This restarts onboarding for this location. It does not reset the agents — they
                    keep their memory of this practice. To start an agent over, unassign and
                    reassign it (Reset &amp; rehire) on the Agents tab.
                  </p>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmRestart(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    loading={startMutation.isPending}
                    onClick={() => startMutation.mutate('restart')}
                  >
                    Confirm Reset
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRestart(true)}
                  disabled={startMutation.isPending}
                >
                  Hard Reset
                </Button>
              ))}
          </div>
        </>
      )}
    </div>
  );
}
